/**
 * Панель привязки Telegram: статус, поиск через TG Client, генерация ссылки
 * Дата: 28.12.2025 - Создан для TODO-016 (Messages UI Improvements)
 * Дата: 10.01.2026 - Обновлено: поддержка случая когда телефон неизвестен (только TG ID)
 * Дата: 10.02.2026 - Добавлена кнопка "Проверить в Telegram" (TODO-021b inline chat TG binding)
 * Дата: 23.02.2026 - РЕФАКТОРИНГ: последовательный флоу, убран ChannelSelector
 *   Флоу: "Проверить в Telegram" → если не найден → "Сгенерировать ссылку Telegram"
 *   Коллеги копируют ссылку и отправляют вручную в нужный мессенджер (по аналогии с MAX)
 *
 * СВЯЗАННЫЕ МОДУЛИ: ChatWindow.tsx (проброс tenantHasTgClient), usePhoneBinding.ts (usePhoneSearch),
 *   useLinking.ts (useSendTelegramLink, useRequestContact), api/linking.ts, api/phone-binding.ts
 *
 * КАК РАБОТАЕТ:
 * - isLinked → зелёный блок "Telegram привязан"
 * - isTelegramId без телефона → "Запросить контакт в Telegram"
 * - isPhoneKnown, !isLinked:
 *   1. Шаг 1 — кнопка "Проверить в Telegram" (MTProto поиск)
 *   2. Найден → зелёная карточка результата
 *   3. Не найден / ошибка / TG Client недоступен → "Сгенерировать ссылку Telegram"
 *   4. Ссылка сгенерирована → показ ссылки + кнопка "Скопировать"
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui';
import { useSendTelegramLink, useRequestContact } from '@/hooks/useLinking';
import { usePhoneSearch } from '@/hooks/usePhoneBinding';
import { useToast } from '@/hooks/useToast';
import { CheckCircle2, AlertCircle, Copy, Link2, UserPlus, Search, Loader2 } from 'lucide-react';

interface TelegramLinkPanelProps {
  phoneNumber: string;
  isLinked: boolean;
  telegramUsername?: string;
  availableChannels?: string[]; // Оставлено для обратной совместимости (не используется)
  /** Дата: 10.02.2026 - Есть ли у тенанта TG Client для MTProto поиска */
  tenantHasTgClient?: boolean;
}

export function TelegramLinkPanel({ 
  phoneNumber, 
  isLinked, 
  telegramUsername,
  tenantHasTgClient = false,
}: TelegramLinkPanelProps) {
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [linkGenerated, setLinkGenerated] = useState(false);
  
  // 12.02.2026 — Модалка выбора канала для request-contact (TG Bot / TG Client)
  const [isContactChannelModalOpen, setIsContactChannelModalOpen] = useState(false);
  
  const sendLinkMutation = useSendTelegramLink();
  const requestContactMutation = useRequestContact();
  const { showSuccess, showError } = useToast();
  
  // Дата: 10.02.2026 - TODO-021b: Inline phone search через MTProto
  const { search: searchInTg, isSearching: isTgSearching, result: tgSearchResult, error: tgSearchError, reset: resetTgSearch } = usePhoneSearch({
    onFound: (r) => showSuccess(`Найден в Telegram: ${r.first_name || ''}${r.telegram_username ? ` (@${r.telegram_username})` : ''}, ID: ${r.telegram_user_id}`),
    onNotFound: () => showError('Пользователь не найден в Telegram (не зарегистрирован или приватность скрыта)'),
    onError: (err) => showError(`Ошибка поиска: ${err.message}`),
  });

  useEffect(() => {
    resetTgSearch();
    setGeneratedLink(null);
    setLinkGenerated(false);
    setIsContactChannelModalOpen(false);
  }, [phoneNumber, resetTgSearch]);
  
  // Дата: 10.01.2026 - Определяем известен ли телефон
  const isPhoneKnown = /^7\d{10}$/.test(phoneNumber);
  const isTelegramId = phoneNumber.startsWith('tg:') || (!isPhoneKnown && /^\d+$/.test(phoneNumber));
  
  // 23.02.2026 — Определяем показывать ли кнопку "Сгенерировать ссылку"
  // Показываем если: TG Client недоступен ИЛИ поиск дал "не найден" ИЛИ поиск вернул ошибку
  // 23.02.2026 FIX: также при HTTP-ошибке (403, 500 и т.д.) — tgSearchError !== null
  const showGenerateLink = !tenantHasTgClient || 
    (tgSearchResult?.status === 'not_found') ||
    (tgSearchResult?.status === 'error') ||
    (tgSearchResult?.status === 'tg_client_unavailable') ||
    (tgSearchError !== null);
  
  // Если уже привязан
  if (isLinked) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-700">
        <CheckCircle2 className="w-5 h-5" />
        <span className="text-sm font-medium">
          Telegram привязан{telegramUsername && `: @${telegramUsername}`}
        </span>
      </div>
    );
  }
  
  // Дата: 10.01.2026 - Если телефон неизвестен (только TG ID) — показать другой UI
  if (isTelegramId && !isPhoneKnown) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 text-amber-700">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm font-medium">
            Есть Telegram. Остальные мессенджеры не привязаны.
          </span>
        </div>
        <p className="text-xs text-amber-600">
          Телефон пациента неизвестен. Для привязки запросите контакт у пациента.
        </p>
        {/* 12.02.2026 — Кнопка открывает модалку выбора канала (TG Bot / TG Client) */}
        <Button 
          variant="secondary"
          size="sm"
          disabled={requestContactMutation.isPending}
          onClick={() => setIsContactChannelModalOpen(true)}
          testId="btn-request-contact-telegram"
        >
          <UserPlus className="w-4 h-4 mr-1" />
          {requestContactMutation.isPending ? 'Отправка...' : 'Запросить контакт в Telegram'}
        </Button>
        
        {/* 12.02.2026 — Модалка выбора канала для request-contact */}
        {isContactChannelModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-1">Запросить контакт</h3>
              <p className="text-sm text-slate-500 mb-5">Выберите канал для отправки запроса</p>
              
              <div className="space-y-3">
                {/* TG Bot — нативная кнопка «Поделиться контактом» */}
                <button
                  disabled={requestContactMutation.isPending}
                  onClick={() => {
                    const mid = phoneNumber.startsWith('tg:') ? phoneNumber.replace('tg:', '') : phoneNumber;
                    requestContactMutation.mutate(
                      { messenger_id: mid, channel: 'telegram_bot' },
                      {
                        onSuccess: () => { showSuccess('Запрос отправлен через TG Bot'); setIsContactChannelModalOpen(false); },
                        onError: (err: Error) => showError(`Ошибка: ${err.message}`),
                      }
                    );
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🤖</span>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-800">Через TG Bot</div>
                    <div className="text-xs text-slate-500">Нативная кнопка «Поделиться контактом» прямо в боте</div>
                  </div>
                </button>
                
                {/* TG Client — deep link на бота через сообщение в личку */}
                <button
                  disabled={requestContactMutation.isPending}
                  onClick={() => {
                    const mid = phoneNumber.startsWith('tg:') ? phoneNumber.replace('tg:', '') : phoneNumber;
                    requestContactMutation.mutate(
                      { messenger_id: mid, channel: 'telegram_client' },
                      {
                        onSuccess: () => { showSuccess('Запрос отправлен через TG Client'); setIsContactChannelModalOpen(false); },
                        onError: (err: Error) => showError(`Ошибка: ${err.message}`),
                      }
                    );
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">💬</span>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-800">Через TG Client</div>
                    <div className="text-xs text-slate-500">Пациент получит ссылку на бота для подтверждения номера</div>
                  </div>
                </button>
              </div>
              
              <button
                onClick={() => setIsContactChannelModalOpen(false)}
                disabled={requestContactMutation.isPending}
                className="mt-4 w-full py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Генерация ссылки — вызываем API, получаем deep_link (без отправки через мессенджер)
  // 23.02.2026 — генерируем ссылку без выбора канала, коллеги копируют и отправляют сами
  const handleGenerateLink = async () => {
    sendLinkMutation.mutate(
      { phone: phoneNumber, channel: 'whatsapp' }, // channel обязателен для API, ссылка генерируется в любом случае
      {
        onSuccess: (response) => {
          if (response.deep_link) {
            setGeneratedLink(response.deep_link);
            setLinkGenerated(true);
            showSuccess('Ссылка для привязки Telegram создана! Скопируйте и отправьте пациенту.');
          } else {
            showError('Не удалось получить ссылку');
          }
        },
        onError: (error: Error) => {
          showError(`Ошибка генерации: ${error.message}`);
        },
      }
    );
  };
  
  // Копирование ссылки
  const handleCopyLink = async () => {
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink);
      showSuccess('Ссылка скопирована в буфер обмена');
    }
  };
  
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
      {/* Статус */}
      <div className="flex items-center gap-2 text-blue-700">
        <AlertCircle className="w-5 h-5" />
        <span className="text-sm font-medium">Telegram не привязан</span>
      </div>
      
      {/* Шаг 1: Кнопка "Проверить в Telegram" (MTProto поиск) */}
      {isPhoneKnown && (
        <div className="space-y-2">
          {/* Кнопка поиска — основное действие */}
          <div className="group relative inline-block">
            <Button
              onClick={() => tenantHasTgClient && searchInTg(phoneNumber)}
              variant="secondary"
              size="sm"
              disabled={!tenantHasTgClient || isTgSearching}
              testId="btn-search-tg"
            >
            {isTgSearching ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Поиск в Telegram…
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-1" />
                Проверить в Telegram
              </>
            )}
          </Button>
          {/* Tooltip при disabled — когда TG Client не подключён */}
          {!tenantHasTgClient && (
            <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 whitespace-nowrap">
              Подключите Telegram Client в настройках
            </div>
          )}
          </div>
          
          {/* Результат inline поиска — найден */}
          {tgSearchResult && tgSearchResult.status === 'found' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-sm text-green-700 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span>
                Найден: {tgSearchResult.first_name}
                {tgSearchResult.telegram_username && ` (@${tgSearchResult.telegram_username})`}
                {tgSearchResult.telegram_user_id && ` · ID: ${tgSearchResult.telegram_user_id}`}
              </span>
            </div>
          )}
          
          {/* Результат inline поиска — не найден (включая приватность) */}
          {tgSearchResult && tgSearchResult.status === 'not_found' && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-2 text-sm text-neutral-500 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>Не найден в Telegram по номеру {phoneNumber} (не зарегистрирован или приватность скрыта)</span>
            </div>
          )}
          
          {/* HTTP ошибка (403, 500 и т.д.) — показываем и предлагаем сгенерировать ссылку */}
          {tgSearchError && !tgSearchResult && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>Ошибка проверки: {tgSearchError.message}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Шаг 2: Генерация ссылки — появляется если поиск неудачен или TG Client недоступен */}
      {isPhoneKnown && showGenerateLink && !linkGenerated && (
        <div className="space-y-1">
          {!tenantHasTgClient && (
            <p className="text-xs text-blue-600">
              TG Client не подключён — можно отправить ссылку для самостоятельной привязки
            </p>
          )}
          <Button 
            onClick={handleGenerateLink}
            variant="secondary"
            size="sm"
            disabled={sendLinkMutation.isPending}
            testId="btn-generate-link"
          >
            {sendLinkMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Генерация…
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4 mr-1" />
                Сгенерировать ссылку Telegram
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Шаг 3: Ссылка сгенерирована — показать + копировать */}
      {linkGenerated && generatedLink && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={generatedLink}
              readOnly
              className="input flex-1 text-xs"
              data-testid="input-generated-link"
            />
            <Button
              onClick={handleCopyLink}
              variant="ghost"
              size="sm"
              testId="btn-copy-link"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-blue-600">
            Скопируйте ссылку и отправьте пациенту в любой мессенджер
          </p>
        </div>
      )}
      
      {/* MAX не привязан — отдельная панель (отображается в MaxLinkPanel) */}
    </div>
  );
}
